import React, { useState } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ChevronRight, ListRestart } from 'lucide-react';
import PropTypes from 'prop-types';
import { Button } from '@getro/rombo';
import { AutoUpdateModal } from './autoUpdateModal';

export const SmartOptions = ({
  onEnableAutoUpdate,
  updateSmartOption,
  contactsCount,
  isEdit,
  hasAutoUpdateFilters,
  isLoadingRefresh,
  onClickAutoUpdate,
  lastUpdated,
  isDynamicTalentPool,
}) => {
  const [autoUpdateAction, setAutoUpdateAction] = useState();
  const handleUpdateSmartOption = (args) => {
    if (args.action === 'disable' && isEdit) {
      setAutoUpdateAction('disable');
      return;
    }

    updateSmartOption(args);
  };
  const smartOptions = [
    {
      key: 'autoUpdate',
      title: 'Auto-update',
      description: isDynamicTalentPool ? (
        <Box>Auto-update list filters are managed externally and cannot be modified.</Box>
      ) : (
        <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
          <Box>
            Automatically add and removes contacts according to your filters daily, while preserving manual edits. Learn
            more.
          </Box>

          {hasAutoUpdateFilters && isEdit && (
            <Flex sx={{ gap: '8px', alignItems: 'center' }}>
              <Button variant="secondary" size="small" onClick={onClickAutoUpdate} loading={isLoadingRefresh}>
                Run now
              </Button>
              {lastUpdated && (
                <Text color="text.subtle" fontSize="14px">
                  Last ran {lastUpdated}.
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      ),
      icon: ListRestart,
      onClick: () => {
        if (contactsCount > 0) {
          setAutoUpdateAction('enable');
          return;
        }

        onEnableAutoUpdate();
      },
      show: isDynamicTalentPool || hasAutoUpdateFilters,
      disabled: isDynamicTalentPool,
      onEdit: () => handleUpdateSmartOption({ type: 'autoUpdate', action: 'edit' }),
      onDisableMatch: () => handleUpdateSmartOption({ type: 'autoUpdate', action: 'disable' }),
    },
  ];

  return (
    <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
      {smartOptions.map((option) => (
        <Flex
          key={option.title}
          data-testid={option.key}
          sx={{
            gap: '8px',
            color: 'text.main',
            fontSize: '14px',
            width: '100%',
          }}
        >
          <Box
            as={option.icon}
            size="24px"
            sx={{
              color: !option.show ? 'neutral.300' : 'purple.400',
            }}
          />
          <Flex sx={{ flexDirection: 'column', gap: '16px', flex: 1 }}>
            <Flex
              sx={{
                gap: '4px',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Flex sx={{ gap: '4px', flexDirection: 'column', maxWidth: '430px' }}>
                <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                  <Box color="text.dark" fontWeight="500">
                    {option.title}
                  </Box>
                  <Flex
                    sx={{
                      backgroundColor: !option.show ? 'neutral.50' : 'green.600',
                      borderRadius: '100px',
                      p: '4px 12px',
                      color: !option.show ? 'neutral.700' : 'white',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '15px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      height: '20px',
                    }}
                  >
                    {!option.show ? 'off' : 'on'}
                  </Flex>
                </Flex>
                <Box>{option.description}</Box>
              </Flex>
              {option.show && !option.disabled && (
                <Flex sx={{ gap: '16px', color: 'text.main' }}>
                  <Flex
                    data-testid={`${option.key}-disable`}
                    onClick={option.onDisableMatch}
                    sx={{ gap: '8px', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <Box>Disable</Box>
                  </Flex>
                  <Flex
                    data-testid={`${option.key}-edit`}
                    onClick={option.onEdit}
                    sx={{ gap: '8px', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <Box>Edit</Box>
                    <Box as={ChevronRight} size="16px" />
                  </Flex>
                </Flex>
              )}
              {!option.show && !option.disabled && (
                <Flex
                  data-testid={`${option.key}-enable`}
                  onClick={option.onClick}
                  sx={{ color: 'text.main', gap: '8px', alignItems: 'center', cursor: 'pointer' }}
                >
                  <Box>Enable</Box>
                  <Box as={ChevronRight} size="16px" />
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      ))}
      {autoUpdateAction && (
        <AutoUpdateModal
          action={autoUpdateAction}
          onCancel={() => setAutoUpdateAction()}
          onEnable={() => {
            if (autoUpdateAction === 'enable') {
              onEnableAutoUpdate();
            } else {
              updateSmartOption({ type: 'autoUpdate', action: 'disable' });
            }
            setAutoUpdateAction();
          }}
        />
      )}
    </Flex>
  );
};

SmartOptions.propTypes = {
  onEnableAutoUpdate: PropTypes.func.isRequired,
  updateSmartOption: PropTypes.func.isRequired,
  contactsCount: PropTypes.number.isRequired,
  isEdit: PropTypes.bool.isRequired,
  hasAutoUpdateFilters: PropTypes.bool.isRequired,
  isLoadingRefresh: PropTypes.bool,
  onClickAutoUpdate: PropTypes.func,
  lastUpdated: PropTypes.string,
  isDynamicTalentPool: PropTypes.bool,
};

SmartOptions.defaultProps = {
  isLoadingRefresh: false,
  onClickAutoUpdate: null,
  lastUpdated: null,
  isDynamicTalentPool: false,
};
